import React, { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import styled from '@emotion/styled'
import { DashboardLayout } from 'components/layout/LoggedIn/DashboardLayout'
import { COLORS, PAGE_BREAKPOINTS } from 'parameters'
import { CheckmarkIcon, EyeClosed, EyeOpen } from 'components/patterns/icons'
import AnalyticsService from 'services/AnalyticsLite/AnalyticsService'
import { NextPage } from 'next'
import { PageProps } from 'types/page'
import { defaultSSRProps } from 'lib/routing/nextPageUtils'
import { validateEmail, validatePassword } from 'lib'
import { ConvertKeysToType } from 'types'
import { StandardLayout } from 'shared/layout/StandardLayout'
import { getUserClient } from 'services/FionaAPI/clients'
import { HttpConflict } from 'services/FionaAPI/templates/errors'
import { useAuthGrants, useAuthLinks, useAuthState } from 'services/Authentication'
import { FieldWrapper, TextInput } from 'components/inputs'
import { WelcomeIllustration } from 'experiences/account/components/icons'
import { LoadingButton } from 'ui/inputs/Button'

interface InputOptions {
	name: 'firstName' | 'email' | 'password'
	label: string
	type: (value: string) => string
	placeholder: string
	icon: (value: string) => JSX.Element | undefined

	validate(value: string): string | undefined
}

interface InputMap {
	firstName: InputOptions
	email: InputOptions
	password: InputOptions
}

const inputMap: InputMap = {
	firstName: {
		name: 'firstName',
		label: 'First Name',
		type: (): string => 'text',
		placeholder: 'Enter your name',
		validate: (): string => '',
		icon: (): undefined => undefined
	},
	email: {
		name: 'email',
		label: 'Email',
		type: (): string => 'text',
		placeholder: 'Enter your email',
		validate: (value: string): string | undefined => validateEmail(value).errorMessage,
		icon: (): undefined => undefined
	},
	password: {
		name: 'password',
		label: 'Password',
		type: (value: string): string => value,
		placeholder: 'Enter your password',
		validate: (): string => '',
		icon: (value: string): JSX.Element => {
			return value === 'password' ? <EyeOpen color={COLORS.OTHER_GRAY} /> : <EyeClosed color={COLORS.OTHER_GRAY} />
		}
	}
}

interface PasswordValidation {
	text: string
	color: (value: string) => boolean
}

const passwordValidationMap: Record<string, PasswordValidation> = {
	charLength: {
		text: 'At least 8 characters',
		color: (value: string): boolean => validatePassword(value).isLengthValid
	},
	upperCase: {
		text: 'One uppercase letter',
		color: (value: string): boolean => validatePassword(value).hasUppercase
	},
	number: {
		text: 'One number',
		color: (value: string): boolean => validatePassword(value).hasNumber
	}
}

type InpuState = ConvertKeysToType<InputMap, string>

const Register: NextPage<PageProps> = ({ metaTags }): JSX.Element => {
	const [inputState, setInputState] = useState<InpuState>({
		firstName: '',
		password: '',
		email: ''
	})

	const { userType } = useAuthState()
	const { refreshGrants } = useAuthGrants()
	const [errorMessage, setErrorMessage] = useState<string>()
	const [passwordType, setPasswordType] = useState('password')
	const [isSubmitting, setIsSubmitting] = useState(false)
	const { login } = useAuthLinks()

	const router = useRouter()

	const handleInputChange = (event: React.FormEvent<HTMLInputElement>): void => {
		setInputState({
			...inputState,
			[event.currentTarget.name]: event.currentTarget.value
		})
	}

	async function handleSubmit(): Promise<void> {
		const userClient = getUserClient()
		setIsSubmitting(true)

		try {
			const { firstName, email, password } = inputState
			await userClient.upgradeUser({
				userUpgrade: { firstName, email, password }
			})
			AnalyticsService.track('register', {
				category: 'account',
				label: router.pathname
			})
			await refreshGrants()
			window.location.href = '/dashboard'
		} catch (e) {
			setIsSubmitting(false)
			if (e instanceof HttpConflict) {
				login()
			} else {
				setErrorMessage('There was an error creating your account')
			}
		}
	}

	const isFormValid = (): boolean => {
		return validateEmail(inputState.email).isValid && validatePassword(inputState.password).isValid
	}

	useEffect(() => {
		if (userType === 'loggedIn') {
			router.replace('/dashboard')
		}
	}, [router, userType])

	const onPasswordIconClick = (): void => {
		const newPasswordType = passwordType === 'password' ? 'text' : 'password'
		setPasswordType(newPasswordType)
	}

	return (
		<StandardLayout metaTags={metaTags}>
			<DashboardLayout>
				<Wrapper>
					<Form>
						<Title>Let’s create your Fiona account.</Title>
						<p>{errorMessage}</p>
						<form>
							{Object.entries(inputMap).map(([key, value]: [string, InputOptions]) => {
								const stateValue = inputState[key as keyof InpuState]
								return (
									<Row key={key}>
										<FieldWrapper label={value.label} htmlFor={value.name}>
											<TextInput
												name={value.name}
												type={value.type(passwordType)}
												placeholder={value.placeholder}
												value={stateValue || ''}
												onChange={handleInputChange}
												icon={value.icon(passwordType)}
												onIconClick={onPasswordIconClick}
											/>
										</FieldWrapper>
									</Row>
								)
							})}

							<PasswordValidationList>
								<List>
									{Object.entries(passwordValidationMap).map(([key, value]) => {
										const color = value.color(inputState.password) ? COLORS.MALACHITE_GREEN : COLORS.NEW_GRAY
										return (
											<ListItem key={key}>
												<CheckmarkIcon color={color} />
												<ListText color={color}>{value.text}</ListText>
											</ListItem>
										)
									})}
								</List>
							</PasswordValidationList>
							<LoadingButton
								variant="primary"
								size="small"
								loading={isSubmitting}
								onClick={handleSubmit}
								disabled={!isFormValid() || isSubmitting}
							>
								Sign up
							</LoadingButton>
							<LegalText>
								By continuing to use our service and clicking &apos;Sign up&apos; I agree and accept Fiona&apos;s{' '}
								<a target={'_blank'} href={'https://fiona.com/terms'} rel="noreferrer">
									Terms of Service
								</a>
								,{' '}
								<a target={'_blank'} href={'https://fiona.com/privacy'} rel="noreferrer">
									Privacy Policy
								</a>{' '}
								and{' '}
								<a
									rel={'noopener noreferrer nofollow'}
									target={'_blank'}
									href={'/PDF/EngineByMoneylionGLBA.pdf'}
									download
								>
									GLBA Privacy Notice
								</a>
							</LegalText>
							<Already>
								<span>Already have an account?</span>
								<A onClick={login}>Sign in here</A>
							</Already>
						</form>
					</Form>
					<ImageContainer>
						<WelcomeIllustration width={855} height={642} />
					</ImageContainer>
				</Wrapper>
			</DashboardLayout>
		</StandardLayout>
	)
}

export const getServerSideProps = defaultSSRProps

const Title = styled.h2`
	font-size: 30px;
	font-weight: bold;
	margin-bottom: 34px;
	margin-top: 34px;
`

const Wrapper = styled.div`
	display: flex;
	width: 100%;
	margin: 0 auto;
	padding-bottom: 34px;
	@media only screen and (${PAGE_BREAKPOINTS.DESKTOP_SMALL}) {
		flex-direction: column;
		width: 60%;
	}
	@media only screen and (${PAGE_BREAKPOINTS.TABLET_PORTRAIT}) {
		flex-direction: column;
		width: 80%;
	}
	@media only screen and (${PAGE_BREAKPOINTS.MOBILE}) {
		flex-direction: column;
		width: 100%;
	}
`

const Form = styled.div`
	width: 30%;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-direction: column;

	button {
		width: 100%;
	}

	form {
		width: 100%;
	}

	@media only screen and (${PAGE_BREAKPOINTS.DESKTOP_SMALL}) {
		width: 100%;
	}
`

const ImageContainer = styled.div`
	width: 70%;
	display: flex;
	justify-content: flex-end;
	align-items: center;
	padding-left: 5%;

	img {
		width: 100%;
	}

	@media only screen and (${PAGE_BREAKPOINTS.DESKTOP_SMALL}) {
		width: 100%;
		padding-left: 0;
	}
	@media only screen and (${PAGE_BREAKPOINTS.MOBILE}) {
		display: none;
	}
`

const Row = styled.div`
	display: flex;
	flex-direction: column;
	margin-bottom: 16px;
	position: relative;
`

const Already = styled.div`
	display: flex;
	justify-content: center;
	margin-top: 16px;

	span:nth-of-type(1) {
		font-size: 16px;
		color: ${COLORS.SPANISH_GRAY};
		margin-right: 8px;
	}
`

const A = styled.span`
	font-size: 16px;
	color: ${COLORS.MALACHITE_GREEN};
	text-decoration: underline;
	cursor: pointer;
`

const List = styled.ul`
	list-style-type: none;
	padding-left: 0;
`

const ListItem = styled.li`
	display: flex;
	align-items: center;
	font-size: 14px;
	color: ${COLORS.SPANISH_GRAY};
	margin-bottom: 8px;
`

const ListText = styled.span<{ color: string }>`
	margin-left: 10px;
	color: ${({ color }): string => color};
`

const LegalText = styled.p`
	font-size: 14px;
	color: #707070;
	margin-top: 20px;
	text-align: center;

	a {
		color: #707070;
	}
`

const PasswordValidationList = styled.div`
	margin: 24px 0 24px 0;
`

export default Register
